if (!customElements.get('product-rail')) {
  class ProductRail extends HTMLElement {
    connectedCallback() {
      this.previousButton = this.querySelector('[data-rail-prev]');
      this.nextButton = this.querySelector('[data-rail-next]');
      this.controls = this.querySelector('.product-rail__controls');

      this.previousButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));
      this.onScroll = () => this.updateControls();
      this.onResize = () => this.updateControls();
      window.addEventListener('resize', this.onResize);
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this, { childList: true, subtree: true });
      this.update();

      if (this.dataset.currentHandle) this.loadRecentlyViewed();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    update() {
      const nextTrack = this.querySelector('[data-product-rail-track]');
      if (nextTrack !== this.track) {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.track = nextTrack;
        this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      }

      const hasProducts = Boolean(this.track?.querySelector('.grid__item'));
      this.classList.toggle('is-ready', hasProducts);
      if (hasProducts) requestAnimationFrame(() => this.updateControls());
    }

    updateControls() {
      if (!this.track || !this.controls) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      this.controls.hidden = maxScroll <= 2;
      this.previousButton.disabled = this.track.scrollLeft <= 2;
      this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
    }

    move(direction) {
      if (!this.track) return;
      const card = this.track.querySelector('.grid__item');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = (card?.getBoundingClientRect().width || this.track.clientWidth) + gap;
      this.track.scrollBy({
        left: direction * distance,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    readHistory() {
      try {
        const items = JSON.parse(localStorage.getItem('mico-recently-viewed-v1') || '[]');
        if (!Array.isArray(items)) return [];
        const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
        return items.filter((item) =>
          item && typeof item.handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(item.handle) &&
          Number.isFinite(item.seenAt) && item.seenAt > cutoff
        );
      } catch {
        return [];
      }
    }

    async loadRecentlyViewed() {
      const currentHandle = this.dataset.currentHandle;
      const history = this.readHistory();
      const limit = Math.min(Math.max(Number(this.dataset.limit) || 6, 2), 8);
      const previous = [...new Set(history.map((item) => item.handle))]
        .filter((handle) => handle !== currentHandle)
        .slice(0, limit);

      try {
        localStorage.setItem('mico-recently-viewed-v1', JSON.stringify([
          { handle: currentHandle, seenAt: Date.now() },
          ...history.filter((item) => item.handle !== currentHandle),
        ].slice(0, 12)));
      } catch {
        // The storefront still works when the browser blocks storage.
      }

      if (!previous.length) return;

      const root = window.Shopify?.routes?.root || '/';
      const cards = await Promise.all(previous.map(async (handle) => {
        try {
          const url = `${root}products/${encodeURIComponent(handle)}?section_id=recently-viewed-product-card`;
          const response = await fetch(url);
          if (!response.ok) return null;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          return html.querySelector('[data-recently-viewed-card]');
        } catch {
          return null;
        }
      }));

      if (!this.isConnected || !this.track) return;
      cards.filter(Boolean).forEach((card) => this.track.appendChild(document.importNode(card, true)));
      this.update();
    }
  }

  customElements.define('product-rail', ProductRail);
}
